import { Check, CircleAlert, Info, X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { type Aviso, cerrarAviso, suscribirAvisos } from "@/lib/avisos"

/**
 * Donde se pintan los avisos emergentes (ver `lib/avisos.ts`). Se monta UNA vez,
 * en `main.tsx`.
 *
 * ⚠ **Abajo a la derecha y ENCIMA del botón de reportar**, no sobre él: esa esquina
 * es suya (`.flota-abajo`) y un aviso que lo tapara dejaría sin la única forma de
 * contar un fallo justo cuando se está avisando de uno. En el teléfono, a lo
 * ancho. En portal a `body`, por lo de siempre: el área lleva `backdrop-filter`.
 *
 * ⚠ **Opacos**, como todo lo que flota encima del cristal (reglas del frontend):
 * translúcido, su texto se leería sobre lo que tapa.
 *
 * ⚠ **La región viva es la LISTA, y existe siempre.** Un `aria-live` que nace con el
 * aviso no se anuncia: el lector sólo vigila regiones que ya estaban en la página.
 * El error va además como `role="alert"`, que interrumpe; el resto, `status`.
 */
export function PilaAvisos() {
  const [avisos, setAvisos] = useState<Aviso[]>([])
  useEffect(() => suscribirAvisos(setAvisos), [])

  return createPortal(
    <ol
      aria-live="polite"
      aria-label="Avisos"
      className="no-print pointer-events-none fixed inset-x-3 bottom-[calc(1.25rem+3rem+0.75rem+env(safe-area-inset-bottom))] z-[60] flex flex-col gap-2 sm:inset-x-auto sm:right-[calc(1.25rem+env(safe-area-inset-right))] sm:w-[380px]"
    >
      <AnimatePresence initial={false}>
        {avisos.map((a) => (
          <TarjetaAviso key={a.id} aviso={a} />
        ))}
      </AnimatePresence>
    </ol>,
    document.body,
  )
}

const ICONO = { exito: Check, error: CircleAlert, info: Info }

/** El color de cada tipo, del tema; el informativo, del dorado del acceso. */
const TINTA = {
  exito: "bg-[color-mix(in_oklab,var(--success)_16%,transparent)] text-[var(--success)]",
  error: "bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[var(--destructive)]",
  info: "bg-[rgb(var(--acceso-oro)/0.18)] text-[rgb(var(--acceso-oro-claro))]",
}

function TarjetaAviso({ aviso }: { aviso: Aviso }) {
  const quieto = useReducedMotion()
  // Pausar y reanudar desde los eventos sin rehacer el efecto: el reloj vive aquí.
  const reloj = useRef<{ pausar: () => void; reanudar: () => void } | null>(null)
  const Icono = ICONO[aviso.tipo]

  useEffect(() => {
    if (aviso.duracion === null) return
    // ⚠ **El cierre lo decide un temporizador, NO el final del hilo.** Con «reducir
    // movimiento» el hilo no se anima —acabaría al instante— y el aviso se iría
    // antes de poder leerse. El hilo sólo enseña cuánto queda.
    let restante = aviso.duracion
    let desde = Date.now()
    let temporizador = setTimeout(() => cerrarAviso(aviso.id), restante)
    reloj.current = {
      // Con el puntero o el foco encima, el aviso espera: quien lo está leyendo —o
      // va a pulsar «Deshacer»— no puede perderlo a mitad. El hilo se pausa a la vez,
      // por CSS (`.aviso-emergente` en `globales.css`).
      pausar: () => {
        clearTimeout(temporizador)
        restante -= Date.now() - desde
      },
      reanudar: () => {
        desde = Date.now()
        temporizador = setTimeout(() => cerrarAviso(aviso.id), restante)
      },
    }
    return () => clearTimeout(temporizador)
  }, [aviso.duracion, aviso.id])

  return (
    <motion.li
      role={aviso.tipo === "error" ? "alert" : "status"}
      className="aviso-emergente pointer-events-auto relative grid grid-cols-[auto_1fr_auto] items-start gap-2.5 overflow-hidden rounded-[var(--radius-tarjeta)] border border-[var(--border)] bg-[var(--surface-elevated)] py-3 pr-2.5 pl-3 text-sm text-[var(--fg)]"
      style={{ boxShadow: "var(--elev-3), var(--realce)" }}
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, transition: { duration: 0.17, ease: "easeIn" } }}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => reloj.current?.pausar()}
      onMouseLeave={() => reloj.current?.reanudar()}
      onFocus={() => reloj.current?.pausar()}
      onBlur={() => reloj.current?.reanudar()}
    >
      <span className={`grid size-7 place-items-center rounded-full ${TINTA[aviso.tipo]}`}>
        <Icono size={16} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="font-semibold">{aviso.titulo}</p>
        {aviso.texto && <p className="mt-0.5 text-[var(--fg-muted)]">{aviso.texto}</p>}
        {(aviso.codigo || aviso.accion) && (
          <div className="mt-1.5 flex items-center gap-2.5">
            {aviso.codigo && (
              <span className="rounded bg-[rgb(var(--marco-tinta)/0.08)] px-1.5 py-px font-mono text-[11px] text-[var(--fg-muted)]">
                {aviso.codigo}
              </span>
            )}
            {aviso.accion && (
              <button
                type="button"
                className="foco-cristal rounded font-semibold text-[rgb(var(--acceso-oro-claro))] hover:underline"
                onClick={() => {
                  cerrarAviso(aviso.id)
                  aviso.accion?.alHacer()
                }}
              >
                {aviso.accion.etiqueta}
              </button>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label="Cerrar aviso"
        onClick={() => cerrarAviso(aviso.id)}
        className="toque-cristal foco-cristal grid size-7 place-items-center rounded-md text-[var(--fg-muted)] hover:text-[var(--fg)]"
      >
        <X size={14} aria-hidden="true" />
      </button>
      {aviso.duracion !== null && !quieto && (
        // El hilo es una animación CSS (`.hilo-aviso`): se pausa sola con el puntero
        // o el foco encima, sin tener que coordinarla con el temporizador.
        <span
          aria-hidden="true"
          className="hilo-aviso absolute inset-x-0 bottom-0 h-0.5 bg-[linear-gradient(90deg,rgb(var(--acceso-oro)/0.4),rgb(var(--acceso-oro-claro)))]"
          style={{ animationDuration: `${aviso.duracion}ms` }}
        />
      )}
    </motion.li>
  )
}
